package com.eladfinish.bible.contest.data.common

import com.eladfinish.bible.contest.network.decodeUtf16BigEndian
import dev.icerock.moko.resources.AssetResource
import kotlinx.browser.window
import kotlinx.coroutines.CancellableContinuation
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.Int8Array
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import org.w3c.xhr.XMLHttpRequest
import kotlin.js.Promise

/**
 * moko-resources' own web fetch (`AssetResource.getText()`) is `suspend` and decodes the
 * response as UTF-8 (`fetch(url).text()`), but every asset in this app is stored UTF-16 BE with
 * a BOM (see project_assets_utf16 memory) and every call site of [AssetReader.readFile] is
 * synchronous (dozens of use cases across the app, not worth an async refactor). So this reads
 * text via a *synchronous* XHR (the only way to get a blocking read in a browser) and trusts
 * `responseText` directly: per the WHATWG XHR spec, `responseText`'s encoding is determined by
 * BOM-sniffing first (UTF-8/UTF-16LE/UTF-16BE), before any requested charset — confirmed this
 * takes priority over `overrideMimeType` even when a different charset is explicitly requested,
 * so it already gives correctly-decoded UTF-16 text and must NOT be re-decoded by hand (an
 * earlier version of this file tried a manual `x-user-defined` byte-level decode on top of this,
 * which double-decoded already-correct text into mojibake).
 *
 * `readFile(fileName)` deliberately does NOT go through the shared `readFileShared()` default
 * (moko's `SharedRes.assets.getAssetByFilePath`): on Kotlin/JS, the generated `assets.values()`
 * only lists the handful of loose top-level asset files, not anything nested under a category
 * folder (allChapters, mikudLevels, other, ...) — unlike Android/iOS, where the generated
 * `values()` is a flat list of every asset. So that lookup NPEs for every chapter/guide file.
 * Instead this resolves the webpack URL itself via `require.context`, which walks the exact same
 * "assets" directory moko's own generated `require(...)` calls resolve against (this file
 * compiles into the same bundle, so the relative path lines up).
 */
actual class AssetReaderImpl : AssetReader {

    // moko-resources registers JS asset paths with a real "/" (e.g. "allChapters/GEN01.txt"),
    // unlike iOS which flattens with "+" for its app bundle — so this must match "/", not iOS's.
    actual override val fileSeparator: String = "/"

    actual override fun readFile(assetResource: AssetResource): String =
        textCache.getOrPut(assetResource.originalPath) { fetchTextSync(assetResource.originalPath) }

    actual override fun readFile(fileName: String): String {
        val url = resolveAssetUrl(fileName) ?: return ""
        return textCache.getOrPut(url) { fetchTextSync(url) }
    }

    actual override fun list(path: String): List<String> = emptyList()

    /**
     * Warms [textCache] for every distinct chapter file concurrently via async `fetch`, so the
     * blocking [fetchTextSync] calls that follow (one per search result) are cache hits instead
     * of synchronous network round-trips — root/number search results can span dozens of
     * distinct chapters, and doing that many *blocking* XHRs back-to-back freezes the whole tab
     * (single-threaded JS) for the full duration.
     */
    override suspend fun prefetch(fileNames: List<String>) = coroutineScope {
        fileNames.distinct()
            .mapNotNull { fileName -> resolveAssetUrl(fileName)?.takeUnless { it in textCache } }
            .distinct()
            .map { url -> async { textCache[url] = fetchTextAsync(url) } }
            .awaitAll()
        Unit
    }

    actual override fun readBinaryFile(fileName: String): ByteArray {
        val url = resolveAssetUrl(fileName) ?: return ByteArray(0)
        return byteCache.getOrPut(url) { fetchBytesSync(url) }
    }

    private companion object {
        val textCache = mutableMapOf<String, String>()
        val byteCache = mutableMapOf<String, ByteArray>()

        val assetsContext: dynamic =
            js("require.context('./assets', true, /\\.(txt|json|csv|html|ttf)$/)")

        fun resolveAssetUrl(fileName: String): String? {
            val key = "./$fileName"
            val keys = assetsContext.keys() as Array<String>
            if (key !in keys) return null
            return assetsContext(key) as String
        }

        fun fetchTextSync(url: String): String {
            val xhr = XMLHttpRequest()
            xhr.open(method = "GET", url = url, async = false)
            xhr.send()
            return if (xhr.status.toInt() in 200..299) xhr.responseText else ""
        }

        // Non-blocking counterpart of fetchTextSync, used only by prefetch(). fetch()'s
        // Response.text() always decodes as UTF-8 (unlike XHR's responseText, which BOM-sniffs
        // UTF-16) — see the file-level doc comment — so this reads raw bytes and decodes by hand
        // the same way HttpResponseExt.bodyUTF16() does for network responses.
        suspend fun fetchTextAsync(url: String): String {
            val emptyInit: RequestInit = js("({})")
            val response = window.fetch(url, emptyInit).awaitPromise()
            if (!response.ok) return ""
            val bytes = Int8Array(response.arrayBuffer().awaitPromise()).unsafeCast<ByteArray>()
            return decodeUtf16BigEndian(bytes).removePrefix("﻿")
        }

        suspend fun <T> Promise<T>.awaitPromise(): T = suspendCancellableCoroutine { cont: CancellableContinuation<T> ->
            then(
                onFulfilled = { cont.resumeWith(Result.success(it)) },
                onRejected = { cont.resumeWith(Result.failure(it)) },
            )
        }

        // No BOM to sniff on non-text assets (e.g. .ttf), so the x-user-defined charset trick
        // (map each byte 0x00-0xFF to one JS UTF-16 code unit) reliably yields raw bytes here.
        fun fetchBytesSync(url: String): ByteArray {
            val xhr = XMLHttpRequest()
            xhr.open(method = "GET", url = url, async = false)
            xhr.overrideMimeType("text/plain; charset=x-user-defined")
            xhr.send()
            if (xhr.status.toInt() !in 200..299) return ByteArray(0)

            val raw = xhr.responseText
            return ByteArray(raw.length) { i ->
                val code = raw[i].code
                (if (code >= 0xF780) code - 0xF700 else code).toByte()
            }
        }
    }
}
